import { useState, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { UserPlus, Send, Loader2, Check, Copy, Share2, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import strings from "@/lib/strings";
import { API_BASE_URL } from "@/lib/api";

const API = API_BASE_URL;

/**
 * SharePopover
 * ------------
 * Renders the "Invite friends" form. Always shows + copies the inviter's
 * personal referral link (`/r/{code}`), fetched on first open via
 * `GET /api/share/code`. Without a code we hide the link box rather than
 * showing a broken bare-domain URL — copying must always return a valid
 * attributable link.
 *
 * The default textarea message is templated with the live referral link
 * so the user can edit/send it as-is. The backend (`/api/share/recommend`)
 * also splices the link in defensively, so even an edited message keeps
 * attribution intact.
 *
 * Props:
 *   - inboxToken          : free-inbox token (for unauthenticated free
 *                            sessions); ignored when account auth is
 *                            present. Optional.
 *   - triggerClassName    : custom className for the trigger button
 *                            (used by the mobile dropdown menu in
 *                            FreeAliasPage).
 *   - triggerContent      : custom JSX for the trigger button label.
 */
export function SharePopover({ inboxToken, triggerClassName, triggerContent } = {}) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("");
  const [referralLink, setReferralLink] = useState("");
  const [linkLoading, setLinkLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [linkCopied, setLinkCopied] = useState(false);

  // Reset form WITHOUT clearing the cached referralLink — the user
  // session's link doesn't change between sends.
  const resetForm = () => {
    setTo("");
    setMessage(referralLink ? strings.share.defaultMessage(referralLink) : "");
    setSentCount(0);
    setLinkCopied(false);
  };

  // Fetch the referral link the first time the popover opens. Cached for
  // the lifetime of the component.
  useEffect(() => {
    if (!open || referralLink || linkLoading) return;
    let cancelled = false;
    (async () => {
      setLinkLoading(true);
      try {
        const headers = inboxToken ? { "X-Inbox-Token": inboxToken } : undefined;
        const { data } = await axios.get(`${API}/api/share/code`, {
          headers,
          withCredentials: true,
        });
        if (cancelled) return;
        const link = data?.referral_link || "";
        if (link) {
          setReferralLink(link);
          // Re-prefill the textarea now that we have a real link.
          if (!message || !message.trim()) {
            setMessage(strings.share.defaultMessage(link));
          }
        }
      } catch {
        // Swallow — UI hides the link box when referralLink is empty.
      } finally {
        if (!cancelled) setLinkLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCopyLink = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setLinkCopied(true);
      toast.success("Link copied");
      setTimeout(() => setLinkCopied(false), 1500);
    } catch {
      toast.error("Could not copy link");
    }
  };

  /**
   * Native share — only shown when `navigator.share` is available
   * (iPhone/iPad Safari, Android Chrome, some PWAs). Silently no-op on
   * AbortError (the user cancelled the share sheet); only surfaces a
   * toast on actual errors.
   */
  const canNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  const handleNativeShare = async () => {
    if (!referralLink || !canNativeShare) return;
    try {
      await navigator.share({
        title: "QuickInbox invite",
        url: referralLink,
      });
    } catch (err) {
      // User cancelled the share sheet — that's not an error we should
      // surface. AbortError is the standard signal for cancellation.
      if (err && err.name !== "AbortError") {
        toast.error("Share failed");
      }
    }
  };

  const handleSend = async () => {
    const recipients = to
      .split(";")
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    if (recipients.length === 0) {
      toast.error(strings.share.enterRecipient);
      return;
    }

    const invalid = recipients.find((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (invalid) {
      toast.error(strings.share.invalidEmail(invalid));
      return;
    }

    if (!message.trim()) {
      toast.error(strings.share.enterMessage);
      return;
    }

    setSending(true);
    try {
      // Pass the free-inbox token when invoked from a Free Inbox session so
      // the backend can resolve the inviter without an account auth token.
      // Account-authed contexts can omit `inboxToken` — the backend falls
      // back to the cookie-based session.
      const headers = inboxToken ? { "X-Inbox-Token": inboxToken } : undefined;
      await axios.post(`${API}/api/share/recommend`, {
        recipients,
        message: message.trim(),
      }, { headers, withCredentials: true });
      setSentCount(recipients.length);
    } catch {
      toast.error(strings.share.sendFailed);
    } finally {
      setSending(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={triggerClassName || "h-8 px-2.5 text-xs text-zinc-500 hover:text-[#555555]"}
          data-testid="share-button"
        >
          {triggerContent || (
            <>
              <UserPlus size={14} className="mr-1.5" />
              {strings.share.button}
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="end">
        {sentCount > 0 ? (
          /* ── Success State ── */
          <div className="flex flex-col items-center text-center py-3 space-y-3" data-testid="share-success-state">
            <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
              <Check size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#111111]" data-testid="share-success-count">
                {sentCount === 1
                  ? "Invitation sent to 1 person"
                  : `Invitation sent to ${sentCount} people`}
              </p>
              <p className="text-xs text-zinc-500 mt-1">
                Thanks for sharing!
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="text-xs"
              onClick={resetForm}
              data-testid="share-invite-more-button"
            >
              <UserPlus size={13} className="mr-1.5" />
              Invite more
            </Button>
          </div>
        ) : (
          /* ── Form State ── */
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-[#111111]">{strings.share.title}</h3>
              <p className="text-[11px] text-zinc-500 mt-0.5">{strings.share.subtitle}</p>
            </div>

            {/* Personal referral link — shown only when fetched.
                Hidden during loading or when fetch failed (per prompt:
                no broken / partial links). */}
            {referralLink && (
              <div className="space-y-1.5" data-testid="share-link-section">
                <Label className="text-xs text-zinc-500">Your invite link</Label>
                <div className="flex items-center gap-1.5 rounded-md border border-[#E5E5E5] bg-[#F5F5F5] px-2 py-1.5">
                  <LinkIcon size={12} className="text-zinc-500 shrink-0" />
                  <code
                    className="flex-1 text-[11px] font-mono text-[#333333] truncate"
                    data-testid="share-link-text"
                  >
                    {referralLink}
                  </code>
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="text-zinc-500 hover:text-[#111111] shrink-0"
                    aria-label="Copy invite link"
                    title="Copy invite link"
                    data-testid="share-link-copy"
                  >
                    {linkCopied ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
                  </button>
                  {canNativeShare && (
                    <button
                      type="button"
                      onClick={handleNativeShare}
                      className="text-zinc-500 hover:text-[#111111] shrink-0"
                      aria-label="Share invite link"
                      title="Share invite link"
                      data-testid="share-link-native-share"
                    >
                      <Share2 size={12} />
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="share-to" className="text-xs text-zinc-500">{strings.share.toLabel}</Label>
              <Input
                id="share-to"
                type="text"
                placeholder={strings.share.toPlaceholder}
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="text-sm"
                data-testid="share-to-input"
              />
              <p className="text-[10px] text-zinc-500">{strings.share.toHint}</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="share-message" className="text-xs text-zinc-500">{strings.share.messageLabel}</Label>
              <Textarea
                id="share-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={linkLoading ? "Loading your invite link…" : ""}
                className="min-h-[90px] text-sm text-[#555555] resize-none"
                data-testid="share-message-textarea"
              />
            </div>

            <Button
              size="sm"
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white"
              onClick={handleSend}
              disabled={sending}
              data-testid="share-send-button"
            >
              {sending ? (
                <><Loader2 size={14} className="mr-1.5 animate-spin" /> {strings.share.sending}</>
              ) : (
                <><Send size={14} className="mr-1.5" /> {strings.share.send}</>
              )}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
